import AppContext from "PFApp/app_context";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { SavedViewsContextProvider } from "PFApp/components/filters/saved_views_context/saved_views_context";
import { checkFiltersAreClear } from "PFApp/use_filtered_collection/format_filters";
import Toggle from "PFComponents/toggle/toggle";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { PermissionRule } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import TableProperties from "../table_properties";
import TablePropertiesButton from "../table_properties_button";
import Tabs from "../tabs";
import css from "./roles_controls.module.scss";
import { useRolesColumns } from "./use_roles_columns";

export const blockedProperties = ["availability"];
const viewsKey = PageTarget.WorkflowRoles;

const RolesControls = () => {
  const {
    store: {
      pages: {
        workflowPage: {
          roles: {
            data: { meta, loading },
            onFilterChange,
            onFiltersReset,
            ganttView,
            setGanttView,
            columns,
            setColumns,
            groupBy,
            setGroupBy,
            isWorkforceMemberView
          }
        }
      },
      currentProfile
    }
  } = useContext(AppContext);
  const { t } = useTranslation("workflow");
  const portalRef = useRef(null);

  const roleTemplate = currentProfile.templates.find(({ key }) => key === "role");
  const { defaultColumns } = useRolesColumns(blockedProperties);
  const isWorkforceMemberFilterEnabled = useIsPermittedTo(currentProfile)(
    PermissionRule.FilterByWorkforceMember
  );

  const [showFilters, setShowFilters] = useState(false);
  const [showTableProperties, setShowTableProperties] = useState(false);

  const toggleTableProperties = () => setShowTableProperties((prev) => !prev);

  const blockedFilters = useMemo(() => {
    const filters = ["state", "workflow_state"];
    if (!isWorkforceMemberFilterEnabled) {
      filters.push("workforce_member_id");
    }
    return filters;
  }, [isWorkforceMemberFilterEnabled]);
  const noFiltersSelected = useMemo(() => checkFiltersAreClear(meta.filters, blockedFilters), [meta]);

  const columnsToShow = useMemo(
    () =>
      isWorkforceMemberView
        ? defaultColumns
        : defaultColumns.filter(({ workforceMemberView }) => !workforceMemberView),
    [isWorkforceMemberView, defaultColumns]
  );

  return (
    <SavedViewsContextProvider viewsKey={viewsKey}>
      <div ref={portalRef} />
      <div className={css.header}>
        <Tabs qaId="WorkflowPageRolesTabs" />
        <div className={css.controls}>
          <FiltersButton
            filtersAreClear={noFiltersSelected}
            filtersShown={showFilters}
            onClick={() => setShowFilters((prev) => !prev)}
            qaId="WorkflowPageFilterButton"
          />
          {!ganttView && (
            <TablePropertiesButton isActive={showTableProperties} onClick={toggleTableProperties} />
          )}
          <Toggle
            inline
            label={t("parts.roles.ganttView")}
            checked={ganttView}
            onChange={() => setGanttView(!ganttView)}
            qaId="WorkflowPageGanttToggle"
          />
        </div>
        <TableProperties
          isVisible={showTableProperties}
          onCloseClick={toggleTableProperties}
          template={roleTemplate}
          selectedColumns={columns}
          onColumnsChange={setColumns || function () {}}
          defaultColumns={columnsToShow}
          selectedGroupBy={groupBy}
          onGroupByChange={setGroupBy}
          blockedProperties={blockedProperties}
        />
        {showFilters && (
          <div className={css.filters}>
            <FiltersBox
              portalRef={portalRef}
              style={{ position: "relative", width: "100%" }}
              blockedList={blockedFilters}
              isLoading={loading}
              meta={meta}
              handleFilterChange={onFilterChange}
              handleFiltersChange={onFiltersReset}
              clearFilters={onFiltersReset}
              filtersAreClear={noFiltersSelected}
              closeFilters={() => setShowFilters(false)}
              clearRefValue={noFiltersSelected}
              viewsKey={viewsKey}
              allowToClose
            />
          </div>
        )}
      </div>
      {!noFiltersSelected && (
        <div className={css.preview}>
          <FiltersPreview
            meta={meta}
            viewsKey={viewsKey}
            blockedList={blockedFilters}
            filtersAreClear={noFiltersSelected}
            clearFilters={() => onFiltersReset()}
            onFilterChange={onFilterChange}
            onMoreClicked={() => setShowFilters(true)}
          />
        </div>
      )}
    </SavedViewsContextProvider>
  );
};

export default RolesControls;
